import { useEffect, useId, useRef, useState } from 'react';

import { Button, Card, Toggle } from '@/design-system';
import type { ReportInvoiceChoice, ReportRecipient } from '@/entities/report';

import { reportCopy } from '../copy';
import { useInvoiceChoice, type InvoiceEnclosureSetup } from '../useInvoiceChoice';
import { ClientLanguageField } from './ClientLanguageField';
import { InvoiceEnclosure } from './InvoiceEnclosure';

export interface CorrectionConfirmProps {
    /** De versie die je vaststelt. */
    version: number;
    /** De versie die de klant nu met zijn link ziet. */
    releasedVersion: number;
    recipients: readonly ReportRecipient[];
    busy: boolean;
    /** Factuur meesturen; alleen voor wie facturen mag opstellen en uitgeven. */
    invoice?: InvoiceEnclosureSetup;
    /** De talen waarin de klant het bericht kan krijgen; één taal: geen keuze. */
    languages?: readonly string[];
    /**
     * Vaststellen; `notify` is of de klant een bericht krijgt, `invoice` of de factuur
     * daarin meegaat en `language` in welke taal (alleen met een bericht).
     */
    onConfirm: (
        notify: boolean,
        invoice: ReportInvoiceChoice | undefined,
        language?: string,
    ) => void;
    onCancel: () => void;
}

/**
 * Bevestiging van een nieuwe versie van een rapport dat de klant al heeft.
 *
 * Anders dan bij de eerste versie is vaststellen hier geen versturen: de link die de
 * klant al heeft, toont de nieuwe versie zodra je vaststelt. Of hij daar een bericht
 * over krijgt ("er is een bijgewerkte versie") kies je zelf, en standaard niet - een
 * correctie van een tikfout is geen reden voor een mail aan de koper en zijn adviseur.
 *
 * De factuur gaat niet nog een keer mee: heeft de klant haar al, dan staat dat er.
 * Is ze gewijzigd - een nieuwe factuur na een creditering, of een die er bij de eerste
 * verzending nog niet was - dan kan ze mee in het bericht, en zonder bericht niet.
 */
export function CorrectionConfirm({
    version,
    releasedVersion,
    recipients,
    busy,
    invoice,
    languages = ['nl'],
    onConfirm,
    onCancel,
}: CorrectionConfirmProps) {
    const titleId = useId();
    const confirmRef = useRef<HTMLButtonElement>(null);
    const [notify, setNotify] = useState(false);
    const [language, setLanguage] = useState('nl');
    const deliverable = recipients.filter((recipient) => recipient.email !== null).length;
    const enclosure = useInvoiceChoice(invoice, recipients);

    useEffect(() => {
        confirmRef.current?.focus();
    }, []);

    return (
        <Card
            variant="accent"
            padding="lg"
            role="group"
            aria-labelledby={titleId}
            className="mb-[14px]"
        >
            <h2
                id={titleId}
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {reportCopy.correctionTitle(version)}
            </h2>
            <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                {reportCopy.correctionBody(version, releasedVersion)}
            </p>

            <Toggle
                className="mt-2 max-w-[620px]"
                checked={notify}
                disabled={busy || deliverable === 0}
                onCheckedChange={setNotify}
                label={reportCopy.correctionNotify}
                description={reportCopy.correctionNotifyTo(deliverable)}
            />

            {notify ? (
                <ClientLanguageField
                    languages={languages}
                    value={language}
                    onChange={setLanguage}
                    disabled={busy}
                />
            ) : null}

            {invoice && deliverable > 0 ? (
                <InvoiceEnclosure
                    {...enclosure.fields}
                    disabled={busy}
                    blocked={notify ? undefined : reportCopy.invoice.withoutMessage}
                />
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
                <Button
                    ref={confirmRef}
                    variant="primary"
                    size="md"
                    disabled={busy}
                    onClick={() =>
                        onConfirm(
                            notify,
                            notify ? enclosure.choice : undefined,
                            notify && language !== 'nl' ? language : undefined,
                        )
                    }
                >
                    {reportCopy.correctionConfirm(version)}
                </Button>
                <Button variant="outline" size="md" disabled={busy} onClick={onCancel}>
                    {reportCopy.confirmCancel}
                </Button>
            </div>
        </Card>
    );
}
